import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';
import i18n from '../i18n/config';
import { API_ENDPOINTS } from '../config.js';
import logger from '../utils/logger';

export default function Login() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const [form, setForm] = useState({ email: '', password: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user already has a valid session (cookie-based)
  useEffect(() => {
    // Check if user has valid session by calling profile endpoint
    fetch(`${API_ENDPOINTS.PROFILE}`, {
      method: 'GET',
      credentials: 'include', // Include cookies
    })
      .then(res => {
        if (res.ok) {
          // User has valid session, redirect to dashboard
          navigate('/dashboard', { replace: true });
        }
      })
      .catch(() => {
        // No valid session, stay on login page
      });
  }, [navigate]);

  // Check for message from navigation state or URL query parameters
  useEffect(() => {
    // Check URL query parameters first
    const urlParams = new URLSearchParams(location.search);
    const message = urlParams.get('message');
    
    if (message) {
      setError(message);
      // Clear the message from URL
      navigate('/login', { replace: true });
    } else if (location.state?.message) {
      setSuccess(location.state.message);
      // Clear the message from location state
      navigate(location.pathname, { replace: true });
    }
  }, [location.state, location.search, navigate]);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError('');
    setSuccess('');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError(t('auth.enterEmailPassword') || 'Please enter email and password.');
      return;
    }
    setError('');
    setSuccess('');
    try {
      logger.log('[LOGIN] Attempting login to:', API_ENDPOINTS.LOGIN);
      // Login is a public endpoint, so CSRF token is not required
      // But we can get it from health endpoint if available
      const res = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json'
        },
        credentials: 'include', // Include cookies
        body: JSON.stringify({
          email: form.email.trim(),
          password: form.password,
        }),
      });
      logger.log('[LOGIN] Response status:', res.status);
      logger.log('[LOGIN] Response headers:', Object.fromEntries(res.headers.entries()));
      
      let data;
      try {
        data = await res.json();
      } catch (parseError) {
        logger.error('[LOGIN] Failed to parse response:', parseError);
        setError(t('auth.serverError') || 'Server error. Please try again.');
        return;
      }
      
      logger.log('[LOGIN] Response data:', data);
      if (!res.ok) {
        logger.error('[LOGIN] Login failed:', data.error);
        // Provide more specific error messages
        let errorMessage = data.error || t('auth.loginError') || 'Login failed. Please check your credentials.';
        
        // Handle specific error cases
        if (res.status === 401) {
          errorMessage = t('auth.invalidEmailPassword') || 'Invalid email or password.';
        } else if (res.status === 403) {
          errorMessage = t('auth.emailNotVerified') || 'Please verify your email before logging in.';
        } else if (res.status === 429) {
          errorMessage = t('auth.rateLimitExceeded') || 'Too many login attempts. Please try again later.';
        }
        
        setError(errorMessage);
        setSuccess('');
      } else {
        logger.log('[LOGIN] Login successful');
        setSuccess(data.message || t('auth.loginSuccess') || 'Login successful!');
        // Token is now in httpOnly cookie, not in response
        
        // Fetch user profile to get language preference and CSRF token
        fetch(`${API_ENDPOINTS.PROFILE}`, {
          method: 'GET',
          credentials: 'include',
        })
          .then(profileRes => {
            if (!profileRes.ok) {
              logger.warn('[LOGIN] Profile fetch failed, but continuing...');
              return null;
            }
            // Extract CSRF token from response headers for future requests
            const csrfToken = profileRes.headers.get('X-CSRF-Token');
            if (csrfToken) {
              // Store in cache for future use
              import('../utils/csrf').then(({ extractCSRFToken }) => {
                extractCSRFToken(profileRes);
              });
            }
            return profileRes.json();
          })
          .then(profileData => {
            if (profileData?.user?.language) {
              // Set language from user profile if available
              i18n.changeLanguage(profileData.user.language);
              localStorage.setItem('i18nextLng', profileData.user.language);
            }
          })
          .catch((profileError) => {
            logger.warn('[LOGIN] Profile fetch error (non-critical):', profileError);
            // If profile fetch fails, continue anyway
          });
        
        setTimeout(() => navigate('/dashboard'), 500);
      }
    } catch (err) {
      logger.error('[LOGIN] Network or other error:', err);
      setError(t('auth.serverError') || 'Network error. Please check your connection and try again.');
      setSuccess('');
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 10px',
    margin: '6px 0 18px 0',
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    fontSize: 16,
    background: colors.backgroundHover,
    color: colors.text,
    outline: 'none',
    transition: 'border-color 0.2s, background-color 0.2s, color 0.2s',
  };

  return (
    <div style={{minHeight: '100vh', background: colors.backgroundLight, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.3s ease'}}>
      <form onSubmit={handleSubmit} style={{background: colors.card, borderRadius: 16, boxShadow: `0 4px 24px ${colors.shadowHover}`, padding: 36, width: '100%', maxWidth: 400, transition: 'background-color 0.3s ease, box-shadow 0.3s ease'}}>
        <h2 style={{fontSize: 28, fontWeight: 800, color: colors.primary, marginBottom: 24}}>{t('auth.login')}</h2>
        {error && <div style={{color: colors.error, marginBottom: 12}}>{error}</div>}
        {success && <div style={{color: colors.success, marginBottom: 12}}>{success}</div>}
        <label style={{fontWeight: 600, color: colors.text}}>{t('auth.email')}</label>
        <input type="email" name="email" value={form.email} onChange={handleChange} style={inputStyle} autoComplete="email" required />
        <label style={{fontWeight: 600, color: colors.text}}>{t('auth.password')}</label>
        <input type="password" name="password" value={form.password} onChange={handleChange} style={inputStyle} autoComplete="current-password" required />
        <div style={{marginBottom: 18, textAlign: 'right'}}>
          <Link to="/forgot-password" style={{color: colors.primary, fontSize: 15, textDecoration: 'none'}}>{t('auth.forgotPassword')}</Link>
        </div>
        <button type="submit" style={{marginTop: 8, width: '100%', background: colors.primary, color: 'white', fontWeight: 700, fontSize: 18, borderRadius: 8, padding: '14px 0', border: 'none', boxShadow: `0 2px 8px ${colors.shadow}`, cursor: 'pointer', transition: 'background-color 0.2s'}} onMouseEnter={(e) => e.target.style.background = colors.primaryHover} onMouseLeave={(e) => e.target.style.background = colors.primary}>{t('auth.login')}</button>
      </form>
    </div>
  );
} 